import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Divider,
  HairlineCard,
  Icons,
  iconSize,
  Stack,
  StatusChip,
  Typography,
  type StatusKind,
} from '@munifocus/ui';
import type {
  CouncilAuthorizationStatus,
  CouncilAuthorizationView,
  Form4NameApprovalStatus,
  Form4NameApprovalView,
} from '@munifocus/contracts-gov';
import {
  useCouncilAuthorizations,
  useForm4NameApprovals,
  useWithdrawCouncilAuthorization,
  useWithdrawForm4NameApproval,
} from '../../lib/clerk-api';
import { formatDate, formatDateTime } from '../../lib/relative-time';
import { useAuth } from '../../providers/AuthProvider';
import {
  CouncilDecisionDialog,
  Form4DecisionDialog,
  RequestCouncilDialog,
  RequestForm4Dialog,
} from './ClerkDecisionDialogs';

export const FORM4_STATUS_KIND: Record<Form4NameApprovalStatus, StatusKind> = {
  pending: 'pending',
  approved: 'approved',
  declined: 'rejected',
  withdrawn: 'neutral',
};

export const COUNCIL_STATUS_KIND: Record<CouncilAuthorizationStatus, StatusKind> = {
  pending: 'pending',
  authorized: 'approved',
  declined: 'rejected',
  withdrawn: 'neutral',
};

function HistoryRow({
  title,
  status,
  lines,
  action,
}: {
  title: string;
  status: React.ReactNode;
  lines: (string | null | false)[];
  action?: React.ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      sx={{ justifyContent: 'space-between', gap: 1, py: 1, alignItems: { sm: 'center' } }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {lines.filter(Boolean).map((line, i) => (
          <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {line}
          </Typography>
        ))}
      </Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
        {status}
        {action}
      </Stack>
    </Stack>
  );
}

/**
 * The Clerk's name on this file's Form 4: what was asked, who answered and
 * when. An officer working the file asks; a clerk answers. A Form 4 is not
 * signed while the answer is waiting or was no.
 */
export function Form4NameSection({ fileId, formId }: { fileId: string; formId?: string }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const approvals = useForm4NameApprovals(fileId);
  const withdraw = useWithdrawForm4NameApproval(fileId);
  const [asking, setAsking] = useState(false);
  const [deciding, setDeciding] = useState<Form4NameApprovalView | null>(null);
  const data = approvals.data;
  if (!data) return null;

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" component="h3">
          {t('gov.clerk.form4.title')}
        </Typography>
        {data.canRequest && (
          <Button size="small" onClick={() => setAsking(true)}>
            {t('gov.clerk.form4.request')}
          </Button>
        )}
      </Stack>
      {data.current === 'declined' && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('gov.clerk.form4.declinedNote')}
        </Alert>
      )}
      {data.current === 'pending' && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('gov.clerk.form4.pendingNote')}
        </Alert>
      )}
      {data.items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('gov.clerk.form4.none')}
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {data.items.map((item) => (
            <HistoryRow
              key={item.id}
              title={t('gov.clerk.requestedBy', {
                name: item.requestedByName,
                at: formatDateTime(item.requestedAt, i18n.language),
              })}
              status={
                <StatusChip
                  status={FORM4_STATUS_KIND[item.status]}
                  label={t(`gov.clerk.form4.status.${item.status}`)}
                />
              }
              lines={[
                item.requestNote,
                Boolean(item.decidedAt && item.decidedByName) &&
                  t('gov.clerk.decidedBy', {
                    name: item.decidedByTitle
                      ? `${item.decidedByName}, ${item.decidedByTitle}`
                      : item.decidedByName,
                    at: formatDateTime(item.decidedAt ?? '', i18n.language),
                  }),
                item.decisionComment,
              ]}
              action={
                item.status === 'pending' ? (
                  <>
                    {data.canDecide && (
                      <Button size="small" variant="contained" onClick={() => setDeciding(item)}>
                        {t('gov.clerk.answer')}
                      </Button>
                    )}
                    {item.requestedById === user?.id && (
                      <Button
                        size="small"
                        disabled={withdraw.isPending}
                        onClick={() => withdraw.mutate(item.id)}
                      >
                        {t('gov.clerk.withdraw')}
                      </Button>
                    )}
                  </>
                ) : null
              }
            />
          ))}
        </Stack>
      )}
      {asking && (
        <RequestForm4Dialog fileId={fileId} formId={formId} onClose={() => setAsking(false)} />
      )}
      {deciding && <Form4DecisionDialog approval={deciding} onClose={() => setDeciding(null)} />}
    </Box>
  );
}

/**
 * Council's authorization of remedial work on this file. Without one (or an
 * emergency bypass) Step 8's work is refused, and this says so plainly.
 */
export function CouncilAuthorizationSection({
  fileId,
  showGateWarning = false,
}: {
  fileId: string;
  /** On Step 8: warn that work cannot be recorded until council authorized it. */
  showGateWarning?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const authorizations = useCouncilAuthorizations(fileId);
  const withdraw = useWithdrawCouncilAuthorization(fileId);
  const [asking, setAsking] = useState(false);
  const [deciding, setDeciding] = useState<CouncilAuthorizationView | null>(null);
  const data = authorizations.data;
  if (!data) return null;

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" component="h3">
          {t('gov.clerk.council.title')}
        </Typography>
        {data.canRequest && (
          <Button size="small" onClick={() => setAsking(true)}>
            {t('gov.clerk.council.request')}
          </Button>
        )}
      </Stack>
      {showGateWarning && !data.remedialWorkAuthorized && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('gov.clerk.council.gateWarning')}
        </Alert>
      )}
      {data.emergencyBypass && (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('gov.clerk.council.bypassNote')}
        </Alert>
      )}
      {data.items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('gov.clerk.council.none')}
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {data.items.map((item) => (
            <HistoryRow
              key={item.id}
              title={`${t(`gov.clerk.council.kinds.${item.kind}`)} · ${t('gov.clerk.requestedBy', {
                name: item.requestedByName,
                at: formatDateTime(item.requestedAt, i18n.language),
              })}`}
              status={
                <StatusChip
                  status={COUNCIL_STATUS_KIND[item.status]}
                  label={t(`gov.clerk.council.status.${item.status}`)}
                />
              }
              lines={[
                item.requestNote,
                Boolean(item.resolutionRef || item.councilMeetingDate) &&
                  t('gov.clerk.council.resolutionLine', {
                    ref: item.resolutionRef ?? '—',
                    date: formatDate(item.councilMeetingDate, i18n.language),
                  }),
                Boolean(item.decidedAt && item.decidedByName) &&
                  t('gov.clerk.decidedBy', {
                    name: item.decidedByName,
                    at: formatDateTime(item.decidedAt ?? '', i18n.language),
                  }),
                item.notes,
              ]}
              action={
                item.status === 'pending' ? (
                  <>
                    {data.canDecide && (
                      <Button size="small" variant="contained" onClick={() => setDeciding(item)}>
                        {t('gov.clerk.council.record')}
                      </Button>
                    )}
                    {item.requestedById === user?.id && (
                      <Button
                        size="small"
                        disabled={withdraw.isPending}
                        onClick={() => withdraw.mutate(item.id)}
                      >
                        {t('gov.clerk.withdraw')}
                      </Button>
                    )}
                  </>
                ) : null
              }
            />
          ))}
        </Stack>
      )}
      {asking && <RequestCouncilDialog fileId={fileId} onClose={() => setAsking(false)} />}
      {deciding && (
        <CouncilDecisionDialog authorization={deciding} onClose={() => setDeciding(null)} />
      )}
    </Box>
  );
}

/** Both of the Clerk's file approvals, as a card on the file page. */
export function ClerkApprovalsPanel({ fileId }: { fileId: string }) {
  const { t } = useTranslation();
  return (
    <HairlineCard sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Icons.BadgeCheck size={iconSize.md} aria-hidden />
        <Typography variant="overline">{t('gov.clerk.panelTitle')}</Typography>
      </Stack>
      <Stack spacing={2} divider={<Divider flexItem />}>
        <Form4NameSection fileId={fileId} />
        <CouncilAuthorizationSection fileId={fileId} />
      </Stack>
    </HairlineCard>
  );
}
